import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LogOut, Plus, Settings } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { AppSidebarTrigger } from "@/components/layout/AppSidebarTrigger";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UserProfileLink } from "@/components/layout/UserProfileLink";
import { HeaderActionsMenu } from "@/components/layout/HeaderActionsMenu";
import { InvoiceUploadOffcanvas } from "@/components/invoices/InvoiceUploadOffcanvas";
import { useHasPermission } from "@/features/auth/useAuth";
import { PERMISSIONS } from "@/features/auth/permissions";
import { useLogout } from "@/features/auth/useLogout";
import { getPageMeta } from "@/lib/navConfig";
import { cn } from "@/lib/utils";

export function AppHeader() {
  const location = useLocation();
  const page = getPageMeta(location.pathname);
  const can = useHasPermission();
  const [uploadOpen, setUploadOpen] = useState(false);
  const { logout, isLoggingOut } = useLogout();

  // Uploading creates an invoice, so `CREATE_INVOICE` is the gate — not
  // `UPDATE_INVOICE`, which this stood behind because the frontend's permission
  // map was missing the create guid and its absence was read as the permission
  // not existing. The two are genuinely different grants: a reviewer who may
  // correct an invoice is not necessarily someone who may bring new ones in.
  //
  // Permission is the only gate. Adding an invoice is a global action, not a
  // property of the queue you happen to be standing on, and requiring the
  // operator to navigate to one particular queue first was a detour with nothing
  // at the end of it — the upload goes to the same place either way.
  const showUpload = can(PERMISSIONS.CREATE_INVOICE);

  return (
    <>
      <header className="page-header">
        <div className="page-header-lead">
          <AppSidebarTrigger className="lg:hidden" />
          <div className="page-header-identity">
            <h1 className="page-header-title">{page.title}</h1>
            <p className="page-header-meta">
              <span>{page.group}</span>
              <span aria-hidden="true">·</span>
              <span>{page.description}</span>
            </p>
          </div>
        </div>

        <div className="page-header-side">
          {showUpload && (
            <Button
              type="button"
              variant="default"
              size="sm"
              className="page-header-add rounded-[0.8rem]"
              onClick={() => setUploadOpen(true)}
              aria-label="New invoice"
            >
              <Plus data-icon="inline-start" />
              {/* Icon-only on phones. The label is what the side group cannot
                  afford there, not the action — and a lone `+` is the
                  conventional mobile form of exactly this control. */}
              <span className="page-header-add-label">New invoice</span>
            </Button>
          )}

          {/* Theme and Settings keep their own place on the bar wherever there is
              room for them. They collapse into the overflow menu only on a phone,
              where the alternative is a header that wraps onto a second row. */}
          <div className="page-header-inline-actions">
            <ThemeToggle />
            <NavLink
              to="/settings"
              aria-label="Settings"
              className={cn(
                buttonVariants({ variant: "ghost", size: "icon-sm" }),
                "touch-target-icon text-muted-foreground",
                location.pathname === "/settings" && "bg-muted text-foreground",
              )}
            >
              <Settings className="size-4" />
            </NavLink>
            <span className="page-header-rule" aria-hidden="true" />
          </div>

          <UserProfileLink />

          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  disabled={isLoggingOut}
                  onClick={logout}
                  aria-label={isLoggingOut ? "Signing out" : "Sign out"}
                  className="page-header-inline-action touch-target-icon text-muted-foreground hover:text-destructive"
                />
              }
            >
              <LogOut className="size-4" />
            </TooltipTrigger>
            <TooltipContent>Sign out</TooltipContent>
          </Tooltip>

          <HeaderActionsMenu className="page-header-overflow" />
        </div>
      </header>

      {showUpload && (
        <InvoiceUploadOffcanvas open={uploadOpen} onOpenChange={setUploadOpen} />
      )}
    </>
  );
}
